import React, { useState, useEffect, useRef } from 'react';
import './UpcomingTraining.css';

const UpcomingTraining = () => {
  const [activeTraining, setActiveTraining] = useState(0);
  const [counters, setCounters] = useState({ dossiers: 0, sectors: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef(null);

  const trainings = [
    {
      id: 1,
      title: 'Africa-Continental Project Development & Financing: Green Investment Workshop',
      strategicObjective: 'Prepare finance-ready continental projects aligned with Nigeria Infrastructure Master Plan, PIDA, AfCFTA, AU Climate Strategy and Africa Carbon Market initiatives. Ensure bankability and ESG compliance.',
      venue: 'Afreximbank Africa Trade Centre (AATC), Abuja',
      date: '7–12 December 2026',
      sectors: ['Renewable Energy', 'Transport Corridors', 'Industrial Parks', 'Carbon Markets', 'Security Infrastructure'],
      outcomes: '15–25 Investment-Ready Dossiers',
      fee: '$1,700',
      includes: ['Breakfast & Lunch', 'Original Certificate', 'Training Materials', 'Executive Accommodation', 'Tax Applicable'],
      facilitators: ['Prof. Ladan M.T.', 'Prof. Sunday', 'Prof. Chris', 'World Bank'],
      participants: ['Private Sector', 'International Agencies', 'Government Officials', 'CSO/NGO', 'Politicians', 'Academia', 'Diplomatic Corps'],
      schedule: [
        { day: 'Monday, 7 December', activity: 'Arrival & Registration' },
        { day: 'Tuesday, 8 December', activity: 'Opening Ceremony & Technical Session' },
        { day: 'Wednesday, 9 December', activity: 'Technical Session (Day 1)' },
        { day: 'Thursday, 10 December', activity: 'Technical Session (Day 2)' },
        { day: 'Friday, 11 December', activity: 'Site Visit: Dangote Refinery' },
        { day: 'Saturday, 12 December', activity: 'Departure' }
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;
      let currentStep = 0;

      const animateCounter = () => {
        if (currentStep < steps) {
          const progress = currentStep / steps;
          const eased = 1 - Math.pow(1 - progress, 3);
          setCounters({
            dossiers: Math.round(eased * 25),
            sectors: Math.round(eased * 5)
          });
          currentStep++;
          setTimeout(animateCounter, stepTime);
        } else {
          setCounters({ dossiers: 25, sectors: 5 });
        }
      };

      animateCounter();
    }
  }, [isVisible]);

  const toggleTraining = (index) => {
    setActiveTraining(activeTraining === index ? -1 : index);
  };

  return (
    <div className="training-page">
      {/* Hero Section */}
      <section 
        className="training-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(43, 15, 5, 0.9) 0%, rgba(74, 51, 38, 0.75) 50%, rgba(43, 15, 5, 0.9) 100%),`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="training-hero-overlay"></div>
        <div className="training-hero-content">
          <span className="training-badge">ABVIG TRAINING</span>
          <h1>Project Development & Financing</h1>
          <p className="training-subtitle">Green Investment Training Workshop</p>
          <div className="training-hero-stats" ref={statsRef}>
            <div className="stat-item">
              <span className="stat-number stat-date">7-12</span>
              <span className="stat-label">December 2026</span>
              <div className="stat-glow"></div>
            </div>
            <div className="stat-item stat-dossiers">
              <span className="stat-number">{counters.dossiers}+</span>
              <span className="stat-label">Investment Dossiers</span>
              <div className="stat-glow"></div>
              <div className="stat-pulse"></div>
            </div>
            <div className="stat-item stat-sectors">
              <span className="stat-number">{counters.sectors}</span>
              <span className="stat-label">Key Sectors</span>
              <div className="stat-glow"></div>
              <div className="stat-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Training List - Accordion Style */}
      <section className="training-list-section">
        <div className="container">
          <h2 className="section-title">Upcoming Training Programs</h2>
          
          {trainings.map((training, index) => (
            <div className={`training-accordion ${activeTraining === index ? 'active' : ''}`} key={training.id}>
              <div className="training-accordion-header" onClick={() => toggleTraining(index)}>
                <div className="accordion-left">
                  <span className="accordion-number">{String(index + 1).padStart(2, '0')}</span>
                  <div className="accordion-title">
                    <h3>{training.title}</h3>
                    <p className="accordion-date">{training.date} • {training.venue}</p>
                  </div>
                </div>
                <span className="accordion-icon">{activeTraining === index ? '−' : '+'}</span>
              </div>
              
              {activeTraining === index && (
                <div className="training-accordion-content">
                  {/* Strategic Objective */}
                  <div className="training-detail-block">
                    <div className="detail-icon">🎯</div>
                    <div className="detail-content">
                      <h4>Strategic Objective</h4>
                      <p>{training.strategicObjective}</p>
                    </div>
                  </div>

                  {/* Info Grid */}
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-icon">📍</span>
                      <div>
                        <h4>Venue</h4>
                        <p>{training.venue}</p>
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">📅</span>
                      <div>
                        <h4>Date</h4>
                        <p>{training.date}</p>
                      </div>
                    </div>
                    <div className="info-item">
                      <span className="info-icon">💰</span>
                      <div>
                        <h4>Training Fee</h4>
                        <p className="fee-amount">{training.fee}</p>
                      </div>
                    </div>
                  </div>

                  {/* What's Included */}
                  <div className="included-section">
                    <h4>📋 What's Included</h4>
                    <div className="included-grid">
                      {training.includes.map((item, idx) => (
                        <span className="included-tag" key={idx}>✓ {item}</span>
                      ))}
                    </div>
                  </div>

                  {/* Facilitators */}
                  <div className="facilitators-section">
                    <h4>👨‍🏫 Facilitators</h4>
                    <div className="facilitators-grid">
                      {training.facilitators.map((facilitator, idx) => (
                        <span className="facilitator-tag" key={idx}>{facilitator}</span>
                      ))}
                    </div>
                  </div>

                  {/* Participants */}
                  <div className="participants-section">
                    <h4>👥 Target Participants</h4>
                    <div className="participants-grid">
                      {training.participants.map((participant, idx) => (
                        <span className="participant-tag" key={idx}>{participant}</span>
                      ))}
                    </div>
                  </div>

                  {/* Schedule */}
                  <div className="schedule-section">
                    <h4>📅 Program Schedule</h4>
                    <div className="schedule-timeline">
                      {training.schedule.map((item, idx) => (
                        <div className="schedule-item" key={idx}>
                          <div className="schedule-day">{item.day}</div>
                          <div className="schedule-activity">{item.activity}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sectors & Outcomes */}
                  <div className="sectors-outcomes-grid">
                    <div className="sectors-box">
                      <h4>🎯 Targeted Sectors</h4>
                      <div className="sector-tags">
                        {training.sectors.map((sector, idx) => (
                          <span className="sector-tag" key={idx}>{sector}</span>
                        ))}
                      </div>
                    </div>
                    <div className="outcomes-box">
                      <h4>📊 Expected Outcomes</h4>
                      <p>{training.outcomes}</p>
                    </div>
                  </div>

                  {/* Register Button */}
                  <div className="training-register">
                    <button className="btn-gold" onClick={() => window.location.href = '/partnership-collaboration'}>
                      Register Now
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="training-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Participate?</h2>
            <p>Register your interest for the Africa-Continental Project Development & Financing Training.</p>
            <button className="btn-gold" onClick={() => window.location.href = '/partnership-collaboration'}>
              Register Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpcomingTraining;